import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileText, Search } from "lucide-react";
import api from "@/lib/api";
import { reportFileName } from "@/lib/date";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter,
} from "@/components/ui/table";

export default function Statements() {
  const [customers, setCustomers] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);

  const toggleOverdue = (v) => {
    setOverdueOnly(v);
    if (selected) openCustomer(selected, v);
  };

  useEffect(() => {
    api.get("/statements/customers")
      .then(({ data }) => setCustomers(data.customers))
      .catch(() => toast.error("Could not load customers"));
  }, []);

  const openCustomer = async (c, only = overdueOnly) => {
    setSelected(c);
    setLoadingDetail(true);
    setDetail(null);
    try {
      const { data } = await api.get("/statements/detail", { params: { key: c.customer_key, overdue_only: only } });
      setDetail(data);
    } catch {
      toast.error("Could not load statement");
    } finally {
      setLoadingDetail(false);
    }
  };

  const download = async (fmt) => {
    if (!selected) return;
    const setBusy = fmt === "pdf" ? setDownloadingPdf : setDownloading;
    setBusy(true);
    try {
      const path = fmt === "pdf" ? "/statements/download-pdf" : "/statements/download";
      const res = await api.get(path, {
        params: { key: selected.customer_key, overdue_only: overdueOnly }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = reportFileName(selected.customer_name, fmt === "pdf" ? "pdf" : "xlsx");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Statement downloaded (${fmt === "pdf" ? "PDF" : "Excel"})`);
    } catch {
      toast.error("Could not download statement");
    } finally {
      setBusy(false);
    }
  };

  const filtered = (customers || []).filter((c) =>
    c.customer_name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Statements</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          View a customer's outstanding invoices and download a formatted Excel statement.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-6">
        {/* Customer list */}
        <Card className="lg:col-span-2 p-4 lg:p-5" data-testid="statement-customer-list">
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search customers…"
              className="pl-9"
              data-testid="statement-search-input"
            />
          </div>
          {!customers ? (
            <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No customers with outstanding invoices.</p>
          ) : (
            <div className="space-y-1 max-h-[560px] overflow-y-auto">
              {filtered.map((c) => (
                <button
                  key={c.customer_key}
                  onClick={() => openCustomer(c)}
                  data-testid={`statement-customer-${c.customer_key}`}
                  className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors ${
                    selected?.customer_key === c.customer_key ? "bg-primary/10" : "hover:bg-muted"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{c.customer_name}</span>
                    <Badge variant="outline" className="shrink-0 text-[10px] uppercase">{c.source}</Badge>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <span className="text-[11px] text-muted-foreground">{c.count} invoice(s)</span>
                    <span className="text-xs font-mono font-semibold">{c.total_display}</span>
                  </div>
                  {c.excluded && (
                    <span className="text-[10px] text-amber-600 dark:text-amber-500">Won't Pay — excluded from reminders</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* Detail */}
        <Card className="lg:col-span-3 overflow-hidden" data-testid="statement-detail-card">
          {!selected ? (
            <div className="p-12 text-center text-muted-foreground flex flex-col items-center gap-3">
              <FileText className="w-8 h-8 opacity-40" />
              <p className="text-sm">Select a customer to view their statement.</p>
            </div>
          ) : (
            <>
              <div className="p-5 lg:p-6 pb-4 flex flex-wrap items-start justify-between gap-3 border-b border-border/60">
                <div className="min-w-0">
                  <h3 className="font-heading text-lg font-medium truncate">{selected.customer_name}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5" data-testid="statement-total">
                    {overdueOnly ? "Total overdue" : "Total outstanding"} <span className="font-mono font-semibold text-foreground">{detail?.total_display ?? selected.total_display}</span>
                    {" · "}{detail?.count ?? selected.count} invoice(s)
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="flex items-center gap-2 text-sm mr-2 cursor-pointer">
                    <Switch checked={overdueOnly} onCheckedChange={toggleOverdue} data-testid="statement-overdue-only-toggle" />
                    Overdue only
                  </label>
                  <Button variant="outline" onClick={() => download("pdf")} disabled={downloadingPdf || !detail?.count} data-testid="statement-download-pdf-button">
                    <FileText className="w-4 h-4 mr-2" />
                    {downloadingPdf ? "Preparing…" : "Download PDF"}
                  </Button>
                  <Button onClick={() => download("xlsx")} disabled={downloading || !detail?.count} data-testid="statement-download-button">
                    <FileDown className="w-4 h-4 mr-2" />
                    {downloading ? "Preparing…" : "Download Excel"}
                  </Button>
                </div>
              </div>
              {loadingDetail || !detail ? (
                <div className="p-6 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
              ) : detail.invoices.length === 0 ? (
                <p className="text-sm text-muted-foreground p-12 text-center" data-testid="statement-empty">
                  {overdueOnly ? "No overdue invoices for this customer." : "No outstanding invoices for this customer."}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50 hover:bg-muted/50">
                        {detail.columns.map((col) => (
                          <TableHead
                            key={col}
                            className={`text-xs uppercase tracking-wider whitespace-nowrap ${col === "amount_display" ? "text-right" : ""}`}
                          >
                            {detail.labels[col]}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.invoices.map((inv, i) => (
                        <TableRow
                          key={i}
                          className={inv.is_overdue ? "bg-yellow-100 hover:bg-yellow-200/70 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/40" : "hover:bg-muted/40"}
                          data-testid={`statement-row-${i}`}
                          data-overdue={inv.is_overdue ? "true" : "false"}
                        >
                          {detail.columns.map((col) => (
                            <TableCell
                              key={col}
                              className={`text-sm whitespace-nowrap ${
                                col === "amount_display" ? "text-right font-mono font-semibold" : "text-muted-foreground"
                              }`}
                            >
                              {col === "days_overdue" ? `${inv[col]} days` : inv[col]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      {[["Total", detail.total_display, "statement-total-row", "border-t-2 border-primary/60 bg-muted/50 hover:bg-muted/50", "statement-total-amount"],
                        [`Overdue Amount (${detail.overdue_count})`, detail.overdue_display, "statement-overdue-row", "bg-yellow-100 hover:bg-yellow-100 dark:bg-yellow-900/30", "statement-overdue-amount"],
                      ].map(([label, amount, tid, cls, amtTid]) => {
                        const n = detail.columns.length;
                        const ai = detail.columns.indexOf("amount_display") > 0 ? detail.columns.indexOf("amount_display") : Math.max(n - 1, 1);
                        return (
                          <TableRow key={tid} className={cls} data-testid={tid}>
                            <TableCell colSpan={ai} className="text-sm font-bold whitespace-nowrap">{label}</TableCell>
                            <TableCell className="text-sm font-bold text-right font-mono whitespace-nowrap" data-testid={amtTid}>{amount}</TableCell>
                            {n - ai - 1 > 0 && <TableCell colSpan={n - ai - 1} />}
                          </TableRow>
                        );
                      })}
                    </TableFooter>
                  </Table>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
